// src/SixSummary-2.js
// [อัปเดต] เพิ่ม CrownOutlined และไฮไลต์แถวสำหรับอันดับ 1-3

import React, { useState, useEffect, useCallback } from 'react';
import { Table, Avatar, Spin, Alert } from 'antd';
import { UserOutlined, CrownOutlined } from '@ant-design/icons'; // [แก้ไข] เพิ่ม CrownOutlined
import { useNavigate } from 'react-router-dom';
import { db } from './Firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore'; 

import './Leaderboard.css';

function SixSummary() {
  const navigate = useNavigate();
  const [leaderboardData, setLeaderboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [leaderboardTitle, setLeaderboardTitle] = useState('ผลการแข่งขันรอบชิงชนะเลิศ รุ่นอายุ 6 ปี');

  // [แก้ไข] เพิ่มพารามิเตอร์ isBackgroundUpdate
  const fetchSummaryData = useCallback(async (isBackgroundUpdate = false) => {
    if (!db) return;
    
    // ถ้าไม่ใช่การอัปเดตเบื้องหลัง ให้แสดง Loading
    if (!isBackgroundUpdate) {
      setLoading(true);
    }
    setError(null);
    
    const questionScores = {}; 
    const userMetaMap = new Map(); 
    const roomNames = ['Room40', 'Room41', 'Room42', 'Room43', 'Room44'];
    const allFetchedUsers = [];

    try {
      // --- 1. ดึงเกณฑ์คะแนน ---
      const roomConfigRef = doc(db, "Competition-Training", "nathadaj", "Admin", "Room");
      const roomConfigSnap = await getDoc(roomConfigRef);

      if (!roomConfigSnap.exists()) {
        throw new Error("ไม่พบเอกสารตั้งค่าเกณฑ์คะแนน (Admin/Room)");
      }
      
      const roomConfigData = roomConfigSnap.data(); 
      for (let i = 1; i <= 20; i++) {
          const scaleField = `y-scale-${i}`;
          if (roomConfigData[scaleField] !== undefined) {
              questionScores[i] = roomConfigData[scaleField] || 0;
          }
      }

      // --- 2. ดึงข้อมูล User Meta ---
      const scoreCollectionRef = collection(db, "Competition-Training");
      const scoreSnapshot = await getDocs(scoreCollectionRef);
      
      scoreSnapshot.forEach((doc) => {
          const data = doc.data();
          const username = data.username;
          if (username) {
              const showSilver = data['show-silver'] || 0;
              const showGold = data['show-gold'] || 0;
              const showDiamond = data['show-diamond'] || 0;
              const totalSteps = showSilver + showGold + showDiamond;
              const metaFullName = (data.name && data.surname) ? `${data.name} ${data.surname}` : data.name || data.surname;
              const school = data.school || ''; 

              userMetaMap.set(username, {
                  level: totalSteps,
                  name: metaFullName,
                  school: school, 
              });
          }
      });

      // --- 3. ดึงข้อมูลผู้ใช้และคำนวณคะแนน ---
      const fetchPromises = roomNames.map(async (roomName) => {
        const roomCorrectAnswers = {}; 
        try {
          const answerDocRef = doc(db, "Competition-Training", "nathadaj", roomName, "Admin");
          const answerDocSnap = await getDoc(answerDocRef);
          if (answerDocSnap.exists()) {
            const adminData = answerDocSnap.data();
            for (let i = 1; i <= 20; i++) {
                const ansField = `z-answer-${i}`;
                if (adminData[ansField] !== undefined) {
                    roomCorrectAnswers[i] = adminData[ansField];
                }
            }
          }
        } catch (ansErr) {
            console.error(`Error fetching answers for ${roomName}:`, ansErr.message);
        }

        const roomUsersCollectionRef = collection(db, "Competition-Training", "nathadaj", roomName);
        const roomUsersSnapshot = await getDocs(roomUsersCollectionRef);

        roomUsersSnapshot.forEach((userDoc) => {
            const username = userDoc.id;
            const data = userDoc.data();
            const hideUser = data.hide || false;
            
            if (hideUser) return; 

            const meta = userMetaMap.get(username); 
            const roomFullName = (data.name && data.surname) ? `${data.name} ${data.surname}` : data.name || data.surname;
            const metaFullName = meta?.name;
            const fullName = roomFullName || metaFullName || username;
            const userLevel = meta?.level || 0; 
            const userSchool = meta?.school || '';

            let calculatedTotalScore = 0;
            for (let x = 1; x <= 20; x++) {
                const correctAns = roomCorrectAnswers[x]; 
                const userAns = data[`answer-${x}`];
                const questScore = questionScores[x] || 0; 
                
                if (correctAns !== undefined && userAns !== undefined && String(userAns) === String(correctAns)) {
                    calculatedTotalScore += questScore;
                }
            }

            allFetchedUsers.push({
                id: `${roomName}_${username}`, 
                name: fullName, 
                school: userSchool, 
                level: userLevel, 
                score: calculatedTotalScore, 
                hide: false, 
            });
        });
      });
      
      await Promise.all(fetchPromises);

      // --- 4. จัดอันดับ ---
      if (allFetchedUsers.length === 0) {
        setLeaderboardData([]);
      } else {
        allFetchedUsers.sort((a, b) => {
          // 1. คะแนนมากสุดขึ้นก่อน
          if (b.score !== a.score) { 
            return b.score - a.score; 
          } 
          // 2. ถ้าคะแนนเท่ากัน ให้เรียงตามชื่อ (ก-ฮ)
          return a.name.localeCompare(b.name, 'th'); 
        });

        const rankedData = allFetchedUsers.map((user, index) => ({
          ...user,
          ranking: index + 1,
        }));
        setLeaderboardData(rankedData);
      }
      
    } catch (err) {
      setError(`เกิดข้อผิดพลาด: ${err.message}`);
    } finally {
      // ถ้าไม่ใช่ Background update ให้ปิด Loading
      if (!isBackgroundUpdate) {
        setLoading(false);
      }
    }
  }, [db]); 

  // [แก้ไข] เพิ่ม setInterval สำหรับ Auto Update
  useEffect(() => {
    if (db) {
      // โหลดครั้งแรก (แสดง Loading)
      fetchSummaryData(false); 
      
      // ตั้งเวลาโหลดทุก 5 วินาที (ไม่แสดง Loading)
      const intervalId = setInterval(() => {
        fetchSummaryData(true); 
      }, 5000);

      return () => clearInterval(intervalId); // เคลียร์เมื่อ component ถูกทำลาย
    }
  }, [db, fetchSummaryData]);

  const columns = [
    { title: 'อันดับ', dataIndex: 'ranking', key: 'ranking', align: 'center', 
      render: (text) => {
        const iconStyle = { marginRight: '8px', fontSize: '1.2em' }; // Style สำหรับมงกุฎ
        // [แก้ไข] เพิ่ม CrownOutlined พร้อมสีตามอันดับ
        if (text === 1) return (<div className="rank-cell gold"><CrownOutlined style={{ ...iconStyle, color: '#FFD700' }} />{text}</div>); // ทอง
        if (text === 2) return (<div className="rank-cell silver"><CrownOutlined style={{ ...iconStyle, color: '#C0C0C0' }} />{text}</div>); // เงิน
        if (text === 3) return (<div className="rank-cell bronze"><CrownOutlined style={{ ...iconStyle, color: '#CD7F32' }} />{text}</div>); // ทองแดง
        return <div className="rank-cell">{text}</div>;
      },
    },
    { title: 'ชื่อ-นามสกุล', dataIndex: 'name', key: 'name', 
      render: (text) => (<div className="name-cell"><Avatar icon={<UserOutlined />} className="avatar" />{text}</div>),
    },
    {
      title: 'โรงเรียน',
      dataIndex: 'school',
      key: 'school',
    },
    { title: 'คะแนนรวม', dataIndex: 'score', key: 'score', align: 'center', 
      render: (text) => (<span className="score-cell">{text.toLocaleString()} คะแนน</span>),
    },
  ];

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-header">
        {/* 1. Logo Container: วางเป็นลำดับแรก เพื่อให้แสดงผลบนสุด */}
        <div className="logo-container">
          <img 
            src="/Wisdom-Logo.png" 
            alt="Wisdom Logo" 
            className="logo-image1"
            onClick={() => fetchSummaryData(false)} 
          />
        </div>
        
        {/* 2. Title Container: วางลำดับต่อมา */}
        <div className="title-container">
          <h1 className="leaderboard-title">
            {leaderboardTitle}
          </h1>

        </div>

        <div className="button-group" style={{ visibility: 'hidden' }}></div> 
      </div>

      {error && (
        <Alert message="ข้อผิดพลาด" description={error} type="error" showIcon style={{ marginBottom: '20px' }} />
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p>กำลังโหลดสรุปคะแนน...</p>
        </div>
      ) : (
        <Table
          columns={columns}
          dataSource={leaderboardData}
          rowKey="id"
          pagination={false}
          bordered
          // [แก้ไข] เพิ่ม rowClassName กลับเข้ามาสำหรับอันดับ 1-3 (สีรุ้ง)
          rowClassName={(record) => {
            if (record.ranking >= 1 && record.ranking <= 3) {
              return `rank-${record.ranking}-row`;
            }
            return '';
          }}
          locale={{ emptyText: 'ไม่มีข้อมูลใน Leaderboard' }}
          className="leaderboard-table"
        />
      )}
    </div>
  );
}

export default SixSummary;